import React, { useState, useCallback } from 'react';
import SVG from 'react-inlinesvg';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';

import { UserData, GroupUserData, UserValidationError } from '@/school-management/types/user-data';
import ValidateUser from '@/school-management/utils/userDataValidationUtils';
import { sanitizeEmail } from '@/admin/utils/validations';
import classNames from '../UsersTable.module.scss';
import binIcon from '@/admin/assets/svg/bin.svg';
import pencilIcon from '@/admin/assets/svg/pencil.svg';

const splitIntoTwoLines = (value: unknown, threshold = 27): React.ReactNode => {
  if (typeof value !== 'string') return value as React.ReactNode;
  const text = value;
  if (text.length <= threshold) return text;

  const breakChars = new Set(['@', '.', '_', '-', '/', '\\', ':']);
  const mid = Math.ceil(text.length / 2);
  const searchRadius = 8;

  let breakAt = mid;
  for (let offset = 0; offset <= searchRadius; offset++) {
    const left = mid - offset;
    const right = mid + offset;

    if (left >= 0 && left < text.length && breakChars.has(text[left])) {
      breakAt = left + 1;
      break;
    }
    if (right >= 0 && right < text.length && breakChars.has(text[right])) {
      breakAt = right + 1;
      break;
    }
  }

  return (
    <span>
      {text.slice(0, breakAt)}
      <br />
      {text.slice(breakAt)}
    </span>
  );
};

interface UserRowProps {
  user: UserData | GroupUserData;
  index: number;
  allowActions?: boolean;
  usersData: UserData[] | GroupUserData[];
  setUsersData?: React.Dispatch<React.SetStateAction<UserData[]>>;
  setDisableSubmitButton?: React.Dispatch<React.SetStateAction<boolean>>;
}

const UserRow: React.FC<UserRowProps> = ({
  user,
  index,
  allowActions,
  usersData,
  setUsersData,
  setDisableSubmitButton,
}) => {
  const { t } = useAdminTranslation();

  const [validationErrors, setValidationErrors] = useState<UserValidationError[]>([]);
  const [editingUser, setEditingUser] = useState<UserData>({
    firstName: '',
    lastName: '',
    email: '',
  });
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const handleUserRemove = useCallback(
    (index: number) => {
      if (setUsersData) setUsersData(prev => prev.filter((_, i) => i !== index));
    },
    [setUsersData]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, field: keyof UserData) => {
      if (editingUser && editingIndex !== null) {
        const value = field === 'email' ? sanitizeEmail(e.target.value) : e.target.value;
        setEditingUser(prev => ({ ...prev!, [field]: value }));
      }
    },
    [editingUser, editingIndex]
  );

  const handleEditClick = (index: number) => {
    setEditingIndex(index);
    if (setDisableSubmitButton) {
      setDisableSubmitButton(true);
    }
    setEditingUser({ ...usersData[index] });
  };

  const saveEdits = (index: number) => {
    if (editingUser !== null) {
      const validationErrors = ValidateUser(editingUser);
      if (validationErrors.length > 0) {
        setValidationErrors(validationErrors);
        return;
      }

      const updatedUsers = usersData.map((user, i) => (i === index ? editingUser : user));
      if (setUsersData) {
        setUsersData(updatedUsers);
      }
      setValidationErrors([]);
      setEditingIndex(null);
      setEditingUser({ firstName: '', lastName: '', email: '' });
      if (setDisableSubmitButton) {
        setDisableSubmitButton(false);
      }
    }
  };

  const findErrorMessage = useCallback(
    (field: string): string | undefined => {
      const error = validationErrors.find(error => error.field === field);
      return error ? error.message : undefined;
    },
    [validationErrors]
  );

  const handleBlur = () => {
    const errors = ValidateUser(editingUser);
    setValidationErrors(errors);
  };

  return (
    <tr
      key={index}
      className={`${classNames.userRow} ${validationErrors.length > 0 ? classNames.errorRow : ''}`}
    >
      {['firstName', 'lastName', 'email'].map(field => (
        <td key={field}>
          {editingIndex === index ? (
            <>
              <input
                type={field === 'email' ? 'email' : 'text'}
                value={editingUser ? editingUser[field] : ''}
                onChange={e => handleInputChange(e, field)}
                className={findErrorMessage(field) ? classNames.errorInput : ''}
                onBlur={handleBlur}
              />
              {findErrorMessage(field) && (
                <span className={classNames.rowError}>
                  {t(`settings.add_students.errors.${findErrorMessage(field)}`)}
                </span>
              )}
            </>
          ) : field === 'firstName' || field === 'lastName' ? (
            splitIntoTwoLines(user[field], 27)
          ) : (
            user[field]
          )}
        </td>
      ))}
      {allowActions && (
        <td className={classNames.actionIcons}>
          <div>
            {editingIndex === index ? (
              <button
                className={classNames.saveButton}
                onClick={() => saveEdits(index)}
                aria-label={t('common.save').toString()}
              >
                {t('common.save')}
              </button>
            ) : (
              <>
                <button
                  className={classNames.buttonSvg}
                  onClick={() => handleEditClick(index)}
                  aria-label={t('editForm.edit').toString()}
                >
                  <SVG src={pencilIcon} />
                </button>
                <button
                  className={classNames.buttonSvg}
                  onClick={() => handleUserRemove(index)}
                  aria-label={t('delete_student.delete').toString()}
                >
                  <SVG src={binIcon} className={classNames.closingBtn} />
                </button>
              </>
            )}
          </div>
        </td>
      )}
      {!allowActions && (
        <td>
          {user.isRegistered
            ? t('editClassStudentStatusActive')
            : t('editClassStudentStatusInactive')}
        </td>
      )}
    </tr>
  );
};

export default UserRow;
